import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { GRIDMASTERS_MARK, ULTIMATELAP_MARK } from "../assets/brandMarks";
import { RULEBOOK } from "../lib/rulebook";
import { lockScroll, unlockScroll } from "../lib/scrollLock";

const MAIN_LINKS = [
  // Client, 2026-10-08: "Grid Masters", not "Home" - on a microsite "Home" read
  // as that microsite's own home.
  { label: "Grid Masters", to: "/" },
  { label: "Ultimate Lap", to: "/events/ultimate-lap" },
  { label: "Speed Gates", to: "/events/speed-gates" },
  { label: "Track Days", to: "/events/track-days" },
  { label: "Corporate Events", to: "/events/corporate-events" },
  // Client, 2026-10-07: Leaderboard removed from the menu (standings to be approached differently later).
  // Client, 2026-10-08: the "Explore" side column (About / Partners / Rounds) is
  // gone; About joins the main list. Client, 2026-10-10: "About Us", above Contact
  // so Contact is the last item.
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

// Brand lockups. Same .gm-lockup recipe, different shield + words. The
// Ultimate Lap microsite carries its own identity (client, 2026-09-08).
// Marks are inlined data URIs (see assets/brandMarks.js) - never a request.
export const BRANDS = {
  gridmasters: { mark: GRIDMASTERS_MARK, text: "GRID MASTERS", to: "/", label: "Grid Masters home" },
  ultimatelap: { mark: ULTIMATELAP_MARK, text: "ULTIMATE LAP", to: "/events/ultimate-lap", label: "Ultimate Lap home" },
};

export default function Nav({ onLogoRef, onWordmarkRef, onMorphRef, subLinks = null, brand = BRANDS.gridmasters, morph = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Nav-logo visibility: on pages with a wordmark hero (`morph` prop: landing
  // and the Ultimate Lap microsite) the Hero owns the lockup via the
  // single-element wordmark scrub (motion/reveals.js initHeroWordmark +
  // body[data-wm-state]). Everywhere else the lockup is simply visible.
  const hasMorph = !!morph;

  // Throttled scroll handler via rAF to avoid state-setting on every scroll event.
  useEffect(() => {
    let raf = 0;
    let last = false;
    const check = () => {
      const next = window.scrollY > 60;
      if (next !== last) {
        last = next;
        setScrolled(next);
      }
      raf = 0;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const ov = overlayRef.current;
    const panel = panelRef.current;
    if (!ov || !panel) return;
    if (open) {
      lockScroll("menu");
      gsap.set(ov, { autoAlpha: 1 });
      gsap.fromTo(
        panel,
        { yPercent: -100 },
        { yPercent: 0, duration: 0.7, ease: "expo.inOut" }
      );
      const lines = ov.querySelectorAll(".gm-menu__link > span");
      gsap.set(lines, { yPercent: 110 });
      gsap.to(lines, {
        yPercent: 0,
        duration: 0.7,
        ease: "expo.out",
        stagger: 0.06,
        delay: 0.25,
      });
      const side = ov.querySelectorAll(".gm-menu__side a, .gm-menu__sub a, .gm-menu__connector");
      gsap.fromTo(
        side,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", stagger: 0.05, delay: 0.55 }
      );
      const foot = ov.querySelectorAll(".gm-menu__foot > *");
      gsap.fromTo(
        foot,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", stagger: 0.06, delay: 0.7 }
      );
      panel.dataset.everOpen = "1";
    } else {
      if (panel.dataset.everOpen !== "1") return;
      gsap.to(panel, {
        yPercent: -100,
        duration: 0.5,
        ease: "expo.in",
        onComplete: () => {
          gsap.set(ov, { autoAlpha: 0 });
          unlockScroll("menu");
        },
      });
    }
  }, [open]);

  // Route change unmounts / re-renders the nav while the close tween may
  // still be running: never leave the menu's lock behind.
  useEffect(() => () => unlockScroll("menu"), []);

  const closeMenu = () => setOpen(false);
  const toggle = () => setOpen((v) => !v);

  useEffect(() => {
    const key = (e) => e.key === "Escape" && open && setOpen(false);
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open]);

  return (
    <>
      <header
        className={`gm-nav ${scrolled ? "is-scrolled" : ""} ${open ? "is-open" : ""} ${hasMorph ? "has-morph" : ""}`}
        data-brand={brand.text === BRANDS.ultimatelap.text ? "ultimatelap" : "gridmasters"}
        data-testid="site-nav"
      >
        <div className="gm-nav__inner">
          <Link to={brand.to} className="gm-nav__logo" data-testid="nav-logo-link" aria-label={brand.label}>
            <span className="gm-nav__wordmark-slot">
              {/* Sizer: the real nav lockup (shield + text). Reserves layout and
                  is what shows on every non-home route. */}
              <span
                ref={onWordmarkRef}
                className="gm-lockup gm-nav__wordmark"
                data-testid="nav-wordmark"
              >
                <img
                  ref={onLogoRef}
                  src={brand.mark}
                  alt=""
                  className="gm-lockup__mark gm-nav__logo-img"
                  width={40}
                  height={40}
                  decoding="sync"
                />
                <span className="gm-lockup__text">{brand.text}</span>
              </span>
              {/* Morph: wordmark-hero pages only. The SAME lockup at giant
                  size, scaled DOWN from the hero placeholder into this slot. */}
              {hasMorph && (
                <span
                  ref={onMorphRef}
                  className="gm-lockup gm-nav__wordmark-morph"
                  data-testid="nav-wordmark-morph"
                  aria-hidden="true"
                >
                  <img src={brand.mark} alt="" className="gm-lockup__mark" decoding="sync" />
                  <span className="gm-lockup__text">{brand.text}</span>
                </span>
              )}
            </span>
          </Link>

          <div className="gm-nav__actions">
            {subLinks && subLinks.length > 0 && (
              <div className="gm-nav__sublinks" data-testid="microsite-subnav">
                {subLinks.map((l) => {
                  const tid = `microsite-sublink-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
                  // Paths route in-app (no reload); in-page hashes stay anchors.
                  return l.href.startsWith("/") && !l.download ? (
                    <Link key={l.href} to={l.href} data-testid={tid} data-primary={l.primary ? "" : undefined}>{l.label}</Link>
                  ) : (
                    <a key={l.href} href={l.href} download={l.download || undefined} className={l.button ? "gm-nav__sublink-btn" : undefined} data-testid={tid} data-primary={l.primary ? "" : undefined}>
                      {l.shortLabel ? (
                        <>
                          <span className="gm-nav__sublink-full">{l.label}</span>
                          <span className="gm-nav__sublink-short">{l.shortLabel}</span>
                        </>
                      ) : l.label}
                    </a>
                  );
                })}
              </div>
            )}
            <button
              className="gm-nav__toggle"
              onClick={toggle}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="menu-toggle"
            >
              <span className={`gm-nav__toggle-bar ${open ? "is-x1" : ""}`}></span>
              <span className={`gm-nav__toggle-bar ${open ? "is-x2" : ""}`}></span>
            </button>
          </div>
        </div>
      </header>

      <div
        className={`gm-menu ${open ? "is-open" : ""}`}
        ref={overlayRef}
        aria-hidden={!open}
        data-testid="full-screen-menu"
      >
        <div className="gm-menu__panel" ref={panelRef}>
          <div className="gm-menu__grid">
            <nav className="gm-menu__main">
              {MAIN_LINKS.map((l, i) => {
                const isUL = l.to === "/events/ultimate-lap";
                return (
                  <div className={`gm-menu__item ${isUL ? "gm-menu__item--ul" : ""}`} key={l.to + i}>
                    <Link
                      to={l.to}
                      className="gm-menu__link"
                      data-testid={`menu-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={closeMenu}
                    >
                      <span>{l.label}</span>
                    </Link>
                    {/* Ultimate Lap quick actions sit on the same row, to the
                        right, joined by a connector (client, 2026-09-08). */}
                    {isUL && (
                      <>
                        <span className="gm-menu__connector" aria-hidden="true" />
                        <div className="gm-menu__sub" data-testid="menu-ul-actions">
                          <Link to="/events/ultimate-lap/enter" className="btn btn--fill" onClick={closeMenu} data-testid="menu-ul-signup">
                            Sign Up
                          </Link>
                          <a href={RULEBOOK.url} download={RULEBOOK.filename} className="btn" onClick={closeMenu} data-testid="menu-ul-rulebook">
                            Download Rulebook
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="gm-menu__foot">
            <div className="tech-num">GRID MASTERS</div>
          </div>
        </div>
      </div>
    </>
  );
}
